import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from '../store/auth/auth.state';
import { UserState } from '../store/users/user.state';

export enum ModuleType {
  CUSTOMER_SERVICE = 'customer-service',
  GENERAL = 'general',
  LOGISTICS = 'logistics'
}

export interface ModuleInfo {
  id: ModuleType;
  name: string;
  route: string;
  icon: string;
  permissions: string[];
  requiredPermissions: string[];
}

@Injectable({
  providedIn: 'root'
})
export class PermissionUtils {
  
  // Mapeo de permisos por módulo actualizado con home en logistics
  private readonly MODULE_PERMISSIONS: Record<ModuleType, string[]> = {
    [ModuleType.CUSTOMER_SERVICE]: [
      'ac_inicio_read',
      'ac_tablero_read',
      'ac_tablero_update',
      'ac_tablero_import',
      'ac_tablero_messages',
      'ac_tablero_delete',
      'ac_ocr_read',
      'ac_ocr_upload',
      'ac_ocr_download',
      'ac_ocr_validate',
      'ac_ocr_delete',
      'ac_ocr_flag',
    ],
    [ModuleType.GENERAL]: [
      'users_create',
      'users_read', 
      'users_update',
      'users_delete',
      'roles_create',
      'roles_read',
      'roles_update',
      'roles_delete',
    ],
    [ModuleType.LOGISTICS]: [
      'dashboard_read', // Home moved to logistics
      'web_envios_read',
      'web_envios_upload_file',
      'web_devoluciones_read',
      'web_devoluciones_upload_file',
      'rutas_read',
      'rutas_create',
      'rutas_update',
      'rutas_delete',
      'conductores_read',
      'conductores_create',
      'conductores_update',
      'conductores_delete',
      'conductores_unlink',
      'farmacias_read',
      'farmacias_create',
      'farmacias_update',
      'farmacias_delete',
      'package_read',
      'package_update',
      'motivos_no_entrega_manage',
    ]
  };

  // Permisos globales (no pertenecen a módulos específicos)
  private readonly GLOBAL_PERMISSIONS: string[] = [
    'perfil_read',
    'perfil_update'
  ];

  // Información de módulos con rutas en español
  private readonly MODULE_INFO: Record<ModuleType, ModuleInfo> = {
    [ModuleType.CUSTOMER_SERVICE]: {
      id: ModuleType.CUSTOMER_SERVICE,
      name: 'Atención al Cliente',
      route: '/admin/atencion-cliente',
      icon: '/svg/fi-rr-customer-service.svg',
      permissions: this.MODULE_PERMISSIONS[ModuleType.CUSTOMER_SERVICE],
      requiredPermissions: ['ac_inicio_read', 'ac_tablero_read', 'ac_ocr_read']
    },
    [ModuleType.GENERAL]: {
      id: ModuleType.GENERAL,
      name: 'General',
      route: '/admin/users',
      icon: '/svg/fi-rr-settings.svg',
      permissions: this.MODULE_PERMISSIONS[ModuleType.GENERAL],
      requiredPermissions: ['users_read', 'roles_read'] // Al menos uno de estos
    },
    [ModuleType.LOGISTICS]: {
      id: ModuleType.LOGISTICS,
      name: 'Logística',
      route: '/admin/home',
      icon: '/svg/fi-delivery.svg',
      permissions: this.MODULE_PERMISSIONS[ModuleType.LOGISTICS],
      requiredPermissions: ['dashboard_read', 'web_envios_read', 'web_devoluciones_read', 'motivos_no_entrega_manage'] // Al menos uno de los accesos de sección principal
    }
  };

  constructor(private store: Store) {}

  /**
   * Obtiene todos los permisos de un módulo específico
   */
  getModulePermissions(module: ModuleType): string[] {
    return [
      ...this.MODULE_PERMISSIONS[module]
    ];
  }

  /**
   * Verifica si un usuario tiene acceso a un módulo específico
   */
  canAccessModule(module: ModuleType): boolean {
    // [PASAJE-PROD] Logística deshabilitada: nadie accede, ni siquiera el superadmin
    if (module === ModuleType.LOGISTICS) return false;

    const authUser = this.store.selectSnapshot(AuthState.getUser);
    if (authUser?.isSuperAdmin) return true;
    const userStateUser = this.store.selectSnapshot(UserState.getUser);
    if (userStateUser?.isSuperAdmin) return true;

    const userPermissions = this.store.selectSnapshot(AuthState.getPermissions);
    const moduleInfo = this.MODULE_INFO[module];

    // Si no hay permisos requeridos, puede acceder (caso customer-service vacío)
    if (moduleInfo.requiredPermissions.length === 0) {
      return true;
    }

    // Verifica si tiene ALGUNO de los permisos requeridos del módulo
    return moduleInfo.requiredPermissions.some(permission =>
      userPermissions.includes(permission)
    );
  }

  /**
   * Obtiene la lista de módulos accesibles para el usuario actual
   */
  getAccessibleModules(): ModuleType[] {
    // Super admin tiene acceso a todos los módulos siempre
    if (this.isSuperAdmin()) {
      // [PASAJE-PROD] Logística deshabilitada: se excluye también para el superadmin
      // return Object.values(ModuleType);
      return Object.values(ModuleType).filter(module => module !== ModuleType.LOGISTICS);
    }
    return Object.values(ModuleType).filter(module => 
      this.canAccessModule(module)
    );
  }

  /**
   * Verifica si el usuario puede acceder a múltiples módulos
   */
  canAccessMultipleModules(): boolean {
    const accessibleModules = this.getAccessibleModules();
    return accessibleModules.length > 1;
  }

  /**
   * Obtiene el único módulo accesible (si solo tiene acceso a uno)
   */
  getSingleAccessibleModule(): ModuleType | null {
    const accessibleModules = this.getAccessibleModules();
    return accessibleModules.length === 1 ? accessibleModules[0] : null;
  }

  /**
   * Verifica si un usuario tiene un permiso específico
   */
  hasPermission(permission: string): boolean {
    if (this.isSuperAdmin()) return true;
    const userPermissions = this.store.selectSnapshot(AuthState.getPermissions);
    return userPermissions.includes(permission);
  }

  /**
   * Verifica si un usuario tiene ALGUNO de los permisos especificados
   */
  hasAnyPermission(permissions: string[]): boolean {
    if (this.isSuperAdmin()) return true;
    const userPermissions = this.store.selectSnapshot(AuthState.getPermissions);
    return permissions.some(permission => userPermissions.includes(permission));
  }

  /**
   * Verifica si un usuario tiene TODOS los permisos especificados
   */
  hasAllPermissions(permissions: string[]): boolean {
    if (this.isSuperAdmin()) return true;
    const userPermissions = this.store.selectSnapshot(AuthState.getPermissions);
    return permissions.every(permission => userPermissions.includes(permission));
  }

  /**
   * Obtiene información completa de un módulo
   */
  getModuleInfo(module: ModuleType): ModuleInfo {
    return this.MODULE_INFO[module];
  }

  /**
   * Obtiene información de todos los módulos accesibles para el usuario
   */
  getAccessibleModulesInfo(): ModuleInfo[] {
    return this.getAccessibleModules().map(module => this.getModuleInfo(module));
  }

  /**
   * Verifica si un permiso pertenece a un módulo específico
   */
  isPermissionFromModule(permission: string, module: ModuleType): boolean {
    return this.MODULE_PERMISSIONS[module].includes(permission) || 
           this.GLOBAL_PERMISSIONS.includes(permission);
  }

  /**
   * Obtiene el módulo al que pertenece un permiso
   */
  getModuleFromPermission(permission: string): ModuleType | null {
    for (const module of Object.values(ModuleType)) {
      if (this.isPermissionFromModule(permission, module)) {
        return module;
      }
    }
    return null;
  }

  /**
   * Obtiene todos los permisos del sistema (para debugging/administración)
   */
  getAllSystemPermissions(): string[] {
    const allModulePermissions = Object.values(this.MODULE_PERMISSIONS).flat();
    return [...new Set([...allModulePermissions, ...this.GLOBAL_PERMISSIONS])];
  }

  /**
   * Verifica si el usuario es SuperAdmin (via flag DB).
   * Consulta tanto AuthState como UserState para evitar problemas de timing.
   */
  isSuperAdmin(): boolean {
    const authUser = this.store.selectSnapshot(AuthState.getUser);
    if (authUser?.isSuperAdmin) return true;
    const userStateUser = this.store.selectSnapshot(UserState.getUser);
    return userStateUser?.isSuperAdmin === true;
  }

  /**
   * Verifica si el usuario puede operar como Admin del tablero: solo SuperAdmin.
   */
  isAdmin(): boolean {
    return this.isSuperAdmin();
  }

  /**
   * Obtiene permisos globales (accesibles desde cualquier módulo)
   */
  getGlobalPermissions(): string[] {
    return [...this.GLOBAL_PERMISSIONS];
  }

  /**
   * Verifica si un permiso es global
   */
  isGlobalPermission(permission: string): boolean {
    return this.GLOBAL_PERMISSIONS.includes(permission);
  }

  /**
   * Devuelve la primera ruta dentro del módulo a la que el usuario tiene acceso.
   * Usa el mismo mapeo que ModuleGuard para mantener consistencia.
   * Fallback: ruta base del módulo.
   */
  getFirstAccessibleRouteForModule(module: ModuleType): string {
    const moduleInfo = this.MODULE_INFO[module];
    const authUser = this.store.selectSnapshot(AuthState.getUser);
    const userStateUser = this.store.selectSnapshot(UserState.getUser);
    if (authUser?.isSuperAdmin || userStateUser?.isSuperAdmin) return moduleInfo.route;

    const userPermissions = this.store.selectSnapshot(AuthState.getPermissions);

    const routesByModule: Record<ModuleType, { route: string; permissions: string[] }[]> = {
      [ModuleType.LOGISTICS]: [
        { route: '/admin/home',         permissions: ['dashboard_read'] },
        { route: '/admin/envios',        permissions: ['web_envios_read'] },
        { route: '/admin/devoluciones',  permissions: ['web_devoluciones_read'] },
        { route: '/admin/configuracion/motivos-no-entrega', permissions: ['motivos_no_entrega_manage'] },
      ],
      [ModuleType.GENERAL]: [
        { route: '/admin/users', permissions: ['users_read'] },
        { route: '/admin/roles', permissions: ['roles_read'] },
      ],
      [ModuleType.CUSTOMER_SERVICE]: [
        { route: '/admin/atencion-cliente',              permissions: ['ac_inicio_read'] },
        { route: '/admin/coordinacion',                  permissions: ['ac_tablero_read'] },
        { route: '/admin/atencion-cliente/importar-pdf', permissions: ['ac_ocr_read'] },
      ],
    };

    const candidates = routesByModule[module] ?? [];
    const first = candidates.find(({ permissions }) =>
      permissions.some(p => userPermissions.includes(p))
    );

    return first?.route ?? moduleInfo.route;
  }
}
